import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/base/Breadcrumb";
import Toast from "@/components/base/Toast";
import ShareBar from "@/pages/news/detail/components/ShareBar";
import RelatedNewsList from "@/pages/news/detail/components/RelatedNewsList";
import { formatViews, getNewsById, getRelatedNews, workList } from "@/utils/anime";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function NewsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const news = id ? getNewsById(id) : undefined;
  const [toast, setToast] = useState<string | null>(null);

  usePageMeta(
    news ? `${news.title} - 78动漫` : "资讯未找到 - 78动漫",
    news ? news.summary : "未找到对应的动漫资讯内容，请返回动漫资讯列表查看最新动态。",
    news ? `${news.tags.join(",")},动漫资讯,78动漫` : "78动漫,动漫资讯",
  );

  if (!news) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <SiteHeader />
        <main className="flex flex-1 items-center justify-center px-4 py-24">
          <div className="flex max-w-md flex-col items-center text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-article-line text-3xl" />
            </span>
            <h1 className="mt-5 font-heading text-xl font-bold text-foreground-950">未找到该资讯</h1>
            <p className="mt-2 text-sm text-foreground-500">这条资讯可能已被移除或链接有误。</p>
            <Link
              to="/news"
              className="mt-6 flex items-center gap-2 rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-arrow-left-line" />
              返回资讯列表
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const related = getRelatedNews(news, 6);
  const recommendedWorks = [...workList].sort((a, b) => b.score - a.score).slice(0, 4);

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <div className="w-full border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-7xl px-4 py-4 md:px-8">
            <Breadcrumb
              items={[
                { label: "动漫资讯", to: "/news" },
                { label: news.title },
              ]}
            />
          </div>
        </div>

        <article className="mx-auto max-w-7xl px-4 py-8 md:px-8 md:py-12">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
            <div className="lg:col-span-8">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
                <i className="ri-price-tag-3-line" />
                {news.category}
              </span>
              <h1 className="mt-4 font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-3xl lg:text-4xl">
                {news.title}
              </h1>
              <div className="mt-4 flex flex-wrap items-center gap-4 border-b border-background-200 pb-5 text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line" />
                  发布时间：{news.dateLabel}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-fire-line" />
                  阅读：{formatViews(news.views)}
                </span>
              </div>

              <div className="mt-6 overflow-hidden rounded-lg bg-background-100">
                <img
                  src={news.cover}
                  alt={`${news.title} 配图`}
                  title={`${news.title} - 78动漫资讯`}
                  className="h-64 w-full object-cover object-top md:h-96"
                />
              </div>

              <div className="mt-8 flex flex-col gap-5">
                {news.content.map((paragraph, index) =>
                  paragraph.startsWith("### ") ? (
                    <h2
                      key={index}
                      className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950 md:text-xl"
                    >
                      <span className="h-5 w-1 rounded-full bg-accent-500" />
                      {paragraph.replace("### ", "")}
                    </h2>
                  ) : (
                    <p key={index} className="text-[15px] leading-loose text-foreground-700 md:text-base">
                      {paragraph}
                    </p>
                  ),
                )}
              </div>

              <div className="mt-8 flex flex-wrap gap-2">
                {news.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-800"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="mt-8">
                <ShareBar title={news.title} onMessage={(message) => setToast(message)} />
              </div>
            </div>

            <aside className="lg:col-span-4">
              <div className="flex flex-col gap-8 lg:sticky lg:top-24">
                <section className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <h2 className="mb-4 flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                    <i className="ri-links-line text-lg text-primary-600" />
                    相关推荐
                  </h2>
                  <RelatedNewsList items={related} currentId={news.id} />
                </section>

                <section className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <h2 className="mb-4 flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                    <i className="ri-fire-line text-lg text-accent-600" />
                    热门作品
                  </h2>
                  <ul className="flex flex-col gap-3">
                    {recommendedWorks.map((work) => (
                      <li key={work.id}>
                        <Link
                          to={`/recommendations/detail/${work.id}`}
                          className="group flex items-center gap-3 rounded-lg p-2 transition-colors hover:bg-background-100 cursor-pointer"
                        >
                          <span className="h-16 w-12 shrink-0 overflow-hidden rounded-md bg-background-100">
                            <img
                              src={work.cover}
                              alt={`${work.title} 封面`}
                              title={`${work.title} - 78动漫作品推荐`}
                              className="h-full w-full object-cover object-top"
                            />
                          </span>
                          <span className="flex flex-1 flex-col">
                            <span className="line-clamp-1 text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-600">
                              {work.title}
                            </span>
                            <span className="mt-1 flex items-center gap-1 text-xs text-accent-600">
                              <i className="ri-star-fill" />
                              {work.score.toFixed(1)}
                              <span className="text-foreground-400">· {work.type}</span>
                            </span>
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </section>
              </div>
            </aside>
          </div>
        </article>
      </main>
      <Toast message={toast} onClose={() => setToast(null)} />
      <SiteFooter />
    </div>
  );
}